import { useRef } from "react";

const FLOW_ACTION_TYPES = new Set(["clarify", "select_company", "select_role", "confirm"]);

function splitCompanyNames(raw) {
  return String(raw || "")
    .split(/\s*,\s*|\s+or\s+/i)
    .map((part) => part.trim().replace(/[?.!]+$/, ""))
    .filter((name) => name.length >= 2 && name.length <= 80 && /[A-Za-z]/.test(name));
}

/** Build selectable company chips when the API reply lists names in prose but omits actions. */
export function inferCompanyActionsFromText(summary) {
  const text = String(summary || "").trim();
  if (!text) return [];

  const patterns = [
    /specify which one:\s*(.+?)\s*\??$/i,
    /specify which .+ company you mean:\s*(.+?)\s*\??$/i,
    /which one(?:\s+did you mean)?:\s*(.+?)\s*\??$/i,
    /choose (?:from )?(?:these|one of):\s*(.+?)\s*\??$/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) {
      const names = splitCompanyNames(match[1]);
      if (names.length >= 2) {
        return names.map((name) => ({
          type: "select_company",
          label: name,
          value: name,
          reason: "Workspace company",
        }));
      }
    }
  }

  return [];
}

export function isFlowAction(action) {
  return FLOW_ACTION_TYPES.has(action?.type);
}

export function filterFlowActions(actions = []) {
  return (actions || []).filter(isFlowAction);
}

/** Remove question paragraphs duplicated as interactive chips. */
export function stripRedundantClarificationText(summary, actions = []) {
  const text = String(summary || "").trim();
  if (!text || !filterFlowActions(actions).length) return text;

  const hasCompanyPick = actions.some((a) => a.type === "select_company");
  const hasClarify = actions.some((a) => a.type === "clarify");

  return text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((paragraph) => {
      if (!paragraph) return false;
      const low = paragraph.toLowerCase();
      if (!low.includes("?")) return true;
      if (
        hasCompanyPick &&
        (low.includes("specify which") ||
          (low.includes("which") &&
            (low.includes("company you mean") ||
              low.includes("company") ||
              low.includes("referring") ||
              low.includes("did you mean"))))
      ) {
        return false;
      }
      if (hasClarify && low.includes("which") && low.includes("did you mean")) {
        return false;
      }
      if (hasClarify && low.includes("pick an option below")) {
        return false;
      }
      return true;
    })
    .join("\n\n");
}

function FlowOptionButton({ action, onClarify, variant = "default", landing = false }) {
  const type = action?.type || "clarify";
  const label = action?.label || action?.value || "Option";
  const value = action?.value || action?.label || "";
  const clickLock = useRef(false);

  const handleClick = () => {
    if (clickLock.current) return;
    clickLock.current = true;
    onClarify?.(value, action);
    window.setTimeout(() => {
      clickLock.current = false;
    }, 600);
  };

  let className =
    "nexus-flow-option w-full text-left rounded-xl border px-3.5 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/40 ";

  if (type === "confirm") {
    className +=
      "nexus-flow-option--confirm border-emerald-500/35 bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm";
  } else if (type === "select_company") {
    className += landing
      ? "nexus-flow-option--company border-cyan-500/25 bg-cyan-500/[0.08] text-slate-100 hover:bg-cyan-500/[0.14]"
      : "nexus-flow-option--company border-[#004f73]/25 bg-[#004f73]/[0.06] text-slate-800 hover:bg-[#004f73]/10";
  } else if (type === "select_role") {
    className += "nexus-flow-option--role border-slate-200 bg-white text-slate-800 hover:border-[#004f73]/35 hover:bg-[#004f73]/5";
  } else {
    className += landing
      ? "nexus-flow-option--clarify border-cyan-500/25 bg-cyan-500/[0.08] text-slate-100 hover:bg-cyan-500/[0.14]"
      : "nexus-flow-option--clarify border-[#004f73]/25 bg-[#004f73]/[0.06] text-slate-800 hover:bg-[#004f73]/10";
  }

  return (
    <button
      type="button"
      className={className}
      title={action?.reason || label}
      onClick={handleClick}
    >
      <span className="nexus-flow-option-label block leading-snug">{label}</span>
      {action?.reason && type !== "confirm" ? (
        <span className="nexus-flow-option-reason block text-[11px] font-normal text-slate-600 mt-0.5">
          {action.reason}
        </span>
      ) : null}
    </button>
  );
}

export default function NexusFlowQuestionPanel({ actions = [], onClarify, landing = false, answerKind = "" }) {
  const flowActions = filterFlowActions(actions);
  if (!flowActions.length) return null;

  const companies = flowActions.filter((a) => a.type === "select_company");
  const roles = flowActions.filter((a) => a.type === "select_role");
  const clarifies = flowActions.filter((a) => a.type === "clarify");
  const confirms = flowActions.filter((a) => a.type === "confirm");

  const title =
    answerKind === "company_disambiguation" || companies.length
      ? "Which company?"
      : roles.length
        ? "Select participant role"
        : confirms.length
          ? "Confirm action"
          : "Choose an option";

  return (
    <div
      className={`nexus-flow-question-panel rounded-xl border p-3.5 space-y-2.5 ${
        landing ? "nexus-flow-question-panel--landing" : ""
      }`}
      role="group"
      aria-label={title}
    >
      <p className="nexus-flow-question-label text-[11px] font-semibold uppercase tracking-wider">
        {title}
      </p>
      {companies.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {companies.map((action, idx) => (
            <FlowOptionButton
              key={`co-${idx}-${action.label}`}
              action={action}
              onClarify={onClarify}
              landing={landing}
            />
          ))}
        </div>
      ) : null}
      {roles.length ? (
        <div className="flex flex-wrap gap-2">
          {roles.map((action, idx) => (
            <FlowOptionButton
              key={`role-${idx}-${action.label}`}
              action={action}
              onClarify={onClarify}
              variant="role"
              landing={landing}
            />
          ))}
        </div>
      ) : null}
      {clarifies.length ? (
        <div className="grid grid-cols-1 gap-2">
          {clarifies.map((action, idx) => (
            <FlowOptionButton
              key={`cl-${idx}-${action.label}`}
              action={action}
              onClarify={onClarify}
              landing={landing}
            />
          ))}
        </div>
      ) : null}
      {confirms.length ? (
        <div className="flex flex-wrap gap-2">
          {confirms.map((action, idx) => (
            <FlowOptionButton
              key={`cf-${idx}-${action.label}`}
              action={action}
              onClarify={onClarify}
              landing={landing}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
